import { CaretDownIcon, CaretUpIcon, CheckIcon } from '@phosphor-icons/react'
import * as Select from '@radix-ui/react-select'
import { useEffect, useState, type JSX } from 'react'
import { getInputDescriptionId, InputContainer, type InputContainerProps } from './input-container'
import * as styles from './select-box.css'

type SelectBoxProps = InputContainerProps & {
  /** Current value of the select */
  value?: string
  /** Callback fired when the value changes */
  onValueChange?: (value: string) => unknown
  /** Placeholder text shown when no option is selected */
  placeholder?: string
  /** Whether the select is disabled */
  disabled?: boolean
  /** HTML name attribute */
  name?: string
  /** Array of options to display in the select */
  options: Array<{
    value: string
    label: string | JSX.Element
    disabled?: boolean
    hideDisabledCheckIcon?: boolean
  }>
}

export function SelectBox({
  label,
  value,
  onValueChange,
  placeholder = 'Select an option',
  disabled = false,
  name,
  id,
  isRequired,
  explicitLabelCondition,
  helperText,
  errorText,
  isError,
  options,
}: SelectBoxProps) {
  const [internalValue, setInternalValue] = useState<string>(value ?? '')
  // The same test `InputContainer` applies to decide whether the label reads as an error.
  const hasError = isError || !!errorText

  useEffect(() => {
    setInternalValue(value ?? '')
  }, [value])

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  return (
    <InputContainer
      id={id}
      label={label}
      isRequired={isRequired}
      explicitLabelCondition={explicitLabelCondition}
      helperText={helperText}
      errorText={errorText}
      isError={isError}
    >
      <div data-disabled={disabled}>
        <Select.Root value={internalValue} onValueChange={handleValueChange} disabled={disabled}>
          <Select.Trigger
            className={styles.selectTrigger}
            id={id}
            aria-label={label}
            value={internalValue}
            data-value={internalValue}
            // `InputContainer` only colours the label, so without this the control itself stays
            // unmarked and an anchored select looks valid beside a text field showing a red border.
            // Keyed off the same test `InputContainer` uses, so a select given only `errorText` does
            // not end up with a red label above an unmarked control.
            data-error={hasError ? 'true' : undefined}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={id && (errorText || helperText) ? getInputDescriptionId(id) : undefined}
          >
            <Select.Value placeholder={placeholder} />
            <Select.Icon>
              <CaretDownIcon className={styles.caretDown} size={16} weight="fill" />
              <CaretUpIcon className={styles.caretUp} size={16} weight="fill" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content
            className={styles.selectContent}
            position="popper"
            side="bottom"
            align="start"
            sideOffset={-21}
            avoidCollisions={false}
          >
            <Select.Viewport className={styles.selectViewport}>
              {options.map(option => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={styles.selectItem}
                  disabled={option.disabled}
                >
                  <Select.ItemText asChild>
                    <span>{option.label}</span>
                  </Select.ItemText>
                  {!option.hideDisabledCheckIcon &&
                    (option.disabled ? (
                      <CheckIcon className={styles.disabledCheckboxIcon} size={16} weight="regular" />
                    ) : (
                      <Select.ItemIndicator>
                        <CheckIcon className={styles.checkboxIcon} size={16} weight="regular" />
                      </Select.ItemIndicator>
                    ))}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      </div>
      {name && <input type="hidden" name={name} value={internalValue} />}
    </InputContainer>
  )
}
